import { normalizeAppearanceOverride } from "../appearance";
import type {
  AppearanceOverrides,
  HoldingSnapshot,
  PortfolioSnapshot,
  QuoteStatus,
} from "../types";
import {
  MAX_DECIMAL_DIGITS,
  MAX_DECIMAL_SCALE,
  MAX_RENDERER_DECIMAL_ABS,
} from "./constants";
import {
  isPositiveDecimalString,
  multiplyDecimalStrings,
  parseDecimalString,
  safeDecimalToNumber,
  type DecimalString,
  type PositiveDecimalString,
} from "./decimal";
import { preferredHoldingDisplayName } from "./identity";
import type {
  HoldingV1,
  LocalPortfolioRecordV1,
  ManualQuoteSetV1,
  ManualQuoteV1,
  PortfolioDocumentV1,
} from "./schema";

const MAX_RENDERER_NUMBER = MAX_RENDERER_DECIMAL_ABS;
const FALLBACK_FARM_UNIT = "1" as PositiveDecimalString;
const MINIMUM_PERSISTED_FARM_UNIT =
  "0.000000000001" as PositiveDecimalString;
const UNLINKED_TICKER = "?";

interface InternalDecimal {
  readonly coefficient: bigint;
  readonly scale: number;
}

export type PortfolioProjectionWarningCode =
  | "missing_quote"
  | "unusable_quote"
  | "cross_currency_without_fx"
  | "cross_currency_cost_basis_unavailable"
  | "quote_set_mismatch"
  | "numeric_projection_clamped"
  | "numeric_projection_rounded"
  | "farm_unit_derived"
  | "farm_unit_persistence_clamped";

export interface PortfolioProjectionWarning {
  readonly code: PortfolioProjectionWarningCode;
  readonly message: string;
  readonly holdingId?: string;
  readonly field?: string;
}

/**
 * State consumed by the current scene renderer. The source document remains
 * untouched: in particular, a derived farm unit is returned separately so the
 * caller can persist it transactionally.
 */
export interface PortfolioRendererProjection {
  readonly portfolio: PortfolioSnapshot;
  readonly appearanceOverrides: AppearanceOverrides;
  readonly warnings: readonly PortfolioProjectionWarning[];
  readonly needsFarmUnitPersistence: boolean;
  readonly farmUnitPersistenceValue: PositiveDecimalString;
}

function parseExact(value: DecimalString): InternalDecimal | null {
  const parsed = parseDecimalString(value);
  return parsed
    ? { coefficient: parsed.coefficient, scale: parsed.scale }
    : null;
}

function powerOfTen(exponent: number): bigint {
  return 10n ** BigInt(exponent);
}

function normalizeExact(decimal: InternalDecimal): InternalDecimal {
  let { coefficient, scale } = decimal;

  while (scale > 0 && coefficient % 10n === 0n) {
    coefficient /= 10n;
    scale -= 1;
  }

  return { coefficient, scale };
}

function multiplyExact(
  left: DecimalString,
  right: DecimalString,
): InternalDecimal | null {
  const parsedLeft = parseExact(left);
  const parsedRight = parseExact(right);
  if (!parsedLeft || !parsedRight) {
    return null;
  }

  try {
    const persistedProduct = multiplyDecimalStrings(left, right);
    const parsedProduct = parseExact(persistedProduct);
    if (parsedProduct) {
      return parsedProduct;
    }
  } catch {
    // A valid input pair may have an exact product outside the persisted
    // contract. Keep calculating with BigInt and clamp only at the renderer.
  }

  return normalizeExact({
    coefficient: parsedLeft.coefficient * parsedRight.coefficient,
    scale: parsedLeft.scale + parsedRight.scale,
  });
}

function addExact(
  values: readonly InternalDecimal[],
): InternalDecimal | null {
  if (values.length === 0) {
    return null;
  }

  const scale = Math.max(...values.map((value) => value.scale));
  const coefficient = values.reduce(
    (sum, value) =>
      sum + value.coefficient * powerOfTen(scale - value.scale),
    0n,
  );

  return normalizeExact({ coefficient, scale });
}

function compareExact(left: InternalDecimal, right: InternalDecimal): number {
  const scale = Math.max(left.scale, right.scale);
  const leftCoefficient =
    left.coefficient * powerOfTen(scale - left.scale);
  const rightCoefficient =
    right.coefficient * powerOfTen(scale - right.scale);

  return leftCoefficient < rightCoefficient
    ? -1
    : leftCoefficient > rightCoefficient
      ? 1
      : 0;
}

function meanOfTwoExact(
  left: InternalDecimal,
  right: InternalDecimal,
): InternalDecimal {
  const scale = Math.max(left.scale, right.scale);
  const sum =
    left.coefficient * powerOfTen(scale - left.scale) +
    right.coefficient * powerOfTen(scale - right.scale);

  // Multiplying by five moves division by two into base ten exactly.
  return normalizeExact({ coefficient: sum * 5n, scale: scale + 1 });
}

function formatUnboundedDecimal(decimal: InternalDecimal): string {
  const normalized = normalizeExact(decimal);
  const negative = normalized.coefficient < 0n;
  const digits = (
    negative ? -normalized.coefficient : normalized.coefficient
  ).toString();
  const padded =
    normalized.scale > 0
      ? digits.padStart(normalized.scale + 1, "0")
      : digits;
  const integerPart =
    normalized.scale > 0 ? padded.slice(0, -normalized.scale) : padded;
  const fractionalPart =
    normalized.scale > 0 ? padded.slice(-normalized.scale) : "";

  return `${negative ? "-" : ""}${integerPart}${
    fractionalPart ? `.${fractionalPart}` : ""
  }`;
}

function roundExactScale(
  decimal: InternalDecimal,
  maximumScale: number,
): InternalDecimal {
  if (decimal.scale <= maximumScale) {
    return normalizeExact(decimal);
  }

  const divisor = powerOfTen(decimal.scale - maximumScale);
  const absolute =
    decimal.coefficient < 0n ? -decimal.coefficient : decimal.coefficient;
  let quotient = absolute / divisor;
  const remainder = absolute % divisor;

  // Half away from zero is deterministic and avoids silently understating a
  // personal growth anchor at the persistence boundary.
  if (remainder * 2n >= divisor) {
    quotient += 1n;
  }

  return normalizeExact({
    coefficient: decimal.coefficient < 0n ? -quotient : quotient,
    scale: maximumScale,
  });
}

function decimalDigitCount(value: string): number {
  return [...value].filter((character) => character >= "0" && character <= "9")
    .length;
}

function toFarmUnitPersistenceValue(
  decimal: InternalDecimal,
): {
  readonly value: PositiveDecimalString;
  readonly clamped: boolean;
} {
  const rounded = roundExactScale(decimal, MAX_DECIMAL_SCALE);
  const formatted = formatUnboundedDecimal(rounded);

  if (
    rounded.coefficient > 0n &&
    decimalDigitCount(formatted) <= MAX_DECIMAL_DIGITS &&
    isPositiveDecimalString(formatted)
  ) {
    return { value: formatted, clamped: false };
  }

  if (decimal.coefficient > 0n && rounded.coefficient === 0n) {
    return {
      value: MINIMUM_PERSISTED_FARM_UNIT,
      clamped: true,
    };
  }

  const maximum = "9".repeat(MAX_DECIMAL_DIGITS);
  return {
    value: maximum as PositiveDecimalString,
    clamped: true,
  };
}

function warningKey(warning: PortfolioProjectionWarning): string {
  return [
    warning.code,
    warning.holdingId ?? "",
    warning.field ?? "",
  ].join("\u0000");
}

function addWarning(
  warnings: PortfolioProjectionWarning[],
  warningKeys: Set<string>,
  warning: PortfolioProjectionWarning,
): void {
  const key = warningKey(warning);
  if (warningKeys.has(key)) {
    return;
  }

  warningKeys.add(key);
  warnings.push(warning);
}

function toBoundedRendererNumber(
  decimal: InternalDecimal | null,
  context: {
    readonly warnings: PortfolioProjectionWarning[];
    readonly warningKeys: Set<string>;
    readonly holdingId?: string;
    readonly field: string;
  },
): number {
  if (!decimal) {
    return 0;
  }

  const divisor = powerOfTen(decimal.scale);
  const negative = decimal.coefficient < 0n;
  const absolute =
    negative ? -decimal.coefficient : decimal.coefficient;
  const maximumCoefficient = BigInt(MAX_RENDERER_NUMBER) * divisor;

  if (absolute > maximumCoefficient) {
    addWarning(context.warnings, context.warningKeys, {
      code: "numeric_projection_clamped",
      holdingId: context.holdingId,
      field: context.field,
      message:
        "렌더러의 안전한 숫자 범위를 넘어 표시값을 최대 안전값으로 제한했습니다.",
    });
    return negative ? -MAX_RENDERER_NUMBER : MAX_RENDERER_NUMBER;
  }

  const formatted = formatUnboundedDecimal(decimal);
  const contractConversion = safeDecimalToNumber(formatted);
  if (contractConversion.success) {
    return contractConversion.value;
  }

  const integerPart = absolute / divisor;
  const fractionalPart = absolute % divisor;
  const projected =
    Number(integerPart) + Number(fractionalPart) / Number(divisor);
  const signed = negative ? -projected : projected;

  if (!Number.isFinite(signed)) {
    addWarning(context.warnings, context.warningKeys, {
      code: "numeric_projection_clamped",
      holdingId: context.holdingId,
      field: context.field,
      message:
        "렌더러에서 표시할 수 없는 숫자를 안전한 기본값으로 대체했습니다.",
    });
    return 0;
  }

  addWarning(context.warnings, context.warningKeys, {
    code: "numeric_projection_rounded",
    holdingId: context.holdingId,
    field: context.field,
    message:
      "정확한 소수를 화면 숫자로 바꾸는 과정에서 표시 정밀도를 반올림했습니다.",
  });
  return signed;
}

function holdingCostBasisExact(holding: HoldingV1): InternalDecimal | null {
  const costBasis = holding.costBasis;
  if (!costBasis) {
    return null;
  }

  if (costBasis.mode === "average") {
    return multiplyExact(holding.quantity, costBasis.averageBuyPrice);
  }

  const lots = costBasis.purchaseLots
    .map((lot) => multiplyExact(lot.quantity, lot.unitPrice))
    .filter((value): value is InternalDecimal => value !== null);

  return lots.length === costBasis.purchaseLots.length
    ? addExact(lots)
    : null;
}

function quoteMarketValueExact(
  holding: HoldingV1,
  quote: ManualQuoteV1 | undefined,
  isCrossCurrency: boolean,
): InternalDecimal | null {
  if (
    !quote ||
    (quote.quoteStatus !== "valid" && quote.quoteStatus !== "stale")
  ) {
    return null;
  }

  if (quote.marketValue !== undefined) {
    // `ManualQuoteSetV1.currency` is the portfolio currency, so an explicit
    // evaluated amount is already usable even when the unit price is not.
    return parseExact(quote.marketValue);
  }

  if (quote.currentPrice !== undefined && !isCrossCurrency) {
    return multiplyExact(holding.quantity, quote.currentPrice);
  }

  return null;
}

function deriveFarmUnitExact(
  marketValues: readonly InternalDecimal[],
): InternalDecimal {
  const positive = marketValues
    .filter(({ coefficient }) => coefficient > 0n)
    .sort(compareExact);

  if (positive.length === 0) {
    return { coefficient: 1n, scale: 0 };
  }

  const middle = Math.floor(positive.length / 2);
  if (positive.length % 2 === 1) {
    return positive[middle] ?? { coefficient: 1n, scale: 0 };
  }

  const left = positive[middle - 1];
  const right = positive[middle];
  return left && right
    ? meanOfTwoExact(left, right)
    : { coefficient: 1n, scale: 0 };
}

function quoteStatus(
  quote: ManualQuoteV1 | undefined,
): QuoteStatus {
  return quote?.quoteStatus ?? "missing";
}

function holdingTicker(holding: HoldingV1): string {
  return holding.ticker ?? UNLINKED_TICKER;
}

function holdingName(holding: HoldingV1): string {
  return preferredHoldingDisplayName(
    holding,
    "이름 미등록 종목",
  );
}

function projectQuoteObservation(
  holding: HoldingV1,
  quote: ManualQuoteV1 | undefined,
): HoldingSnapshot["quoteObservation"] {
  if (
    quote?.source !== "yahoo-finance" ||
    quote.observedAt === undefined
  ) {
    return undefined;
  }

  const holdingMarket = holding.market?.trim().toUpperCase();
  const ticker = holding.ticker?.trim().toUpperCase();
  const market =
    ticker?.endsWith(".KS") ||
    ticker?.endsWith(".KQ") ||
    holdingMarket === "KRX" ||
    holdingMarket === "KOREA" ||
    quote.marketTimeZone === "Asia/Seoul"
      ? "KRX"
      : holdingMarket === "US" ||
          quote.marketTimeZone === "America/New_York"
        ? "US"
        : undefined;

  return {
    observedAt: quote.observedAt,
    ...(market !== undefined ? { market } : {}),
    ...(quote.marketTimeZone !== undefined
      ? { marketTimeZone: quote.marketTimeZone }
      : {}),
    ...(quote.sourceCurrency !== undefined
      ? { sourceCurrency: quote.sourceCurrency }
      : {}),
    ...(quote.valuationCurrency !== undefined
      ? { valuationCurrency: quote.valuationCurrency }
      : {}),
  };
}

function createHoldingSnapshot(
  holding: HoldingV1,
  quote: ManualQuoteV1 | undefined,
  portfolioCurrency: string,
  warnings: PortfolioProjectionWarning[],
  warningKeys: Set<string>,
): {
  readonly snapshot: HoldingSnapshot;
  readonly marketValueExact: InternalDecimal | null;
} {
  const instrumentId = `holding:${holding.holdingId}`;
  const holdingCurrency = holding.currency ?? portfolioCurrency;
  const costCurrency =
    holding.costBasis?.currency ?? holdingCurrency;
  const costBasisInPortfolioCurrency =
    costCurrency === portfolioCurrency;
  const isCrossCurrency = holdingCurrency !== portfolioCurrency;
  const status = quoteStatus(quote);
  const hasTrustedProviderValuation =
    isCrossCurrency &&
    quote?.source === "yahoo-finance" &&
    quote.sourceCurrency === holdingCurrency &&
    quote.valuationCurrency === portfolioCurrency &&
    quote.marketValue !== undefined;

  if (!quote) {
    addWarning(warnings, warningKeys, {
      code: "missing_quote",
      holdingId: holding.holdingId,
      field: "manualQuotes",
      message:
        "시세가 없어 펫은 유지하고 평가액과 오늘 수익률을 비어 있는 상태로 표시합니다.",
    });
  } else if (status === "missing" || status === "new") {
    addWarning(warnings, warningKeys, {
      code: "unusable_quote",
      holdingId: holding.holdingId,
      field: "quoteStatus",
      message:
        "아직 사용할 수 있는 시세가 없어 평가액과 오늘 수익률을 비어 있는 상태로 표시합니다.",
    });
  }

  if (isCrossCurrency && !hasTrustedProviderValuation) {
    addWarning(warnings, warningKeys, {
      code: "cross_currency_without_fx",
      holdingId: holding.holdingId,
      field: "currency",
      message: `${holdingCurrency} 자산의 환율 정보가 없어 평가액과 등락률을 표시하지 않습니다.`,
    });
  }
  if (
    holding.costBasis &&
    !costBasisInPortfolioCurrency
  ) {
    addWarning(warnings, warningKeys, {
      code: "cross_currency_cost_basis_unavailable",
      holdingId: holding.holdingId,
      field: "costBasis",
      message: `${holdingCurrency} 자산은 현재 환율 기준 평가액과 오늘 등락만 표시합니다. 매수 당시 환율이 없어 원가 수익은 계산하지 않습니다.`,
    });
  }

  const marketValueExact = quoteMarketValueExact(
    holding,
    quote,
    isCrossCurrency,
  );
  const marketValue = toBoundedRendererNumber(marketValueExact, {
    warnings,
    warningKeys,
    holdingId: holding.holdingId,
    field: "marketValue",
  });
  const quantity = toBoundedRendererNumber(parseExact(holding.quantity), {
    warnings,
    warningKeys,
    holdingId: holding.holdingId,
    field: "quantity",
  });
  const currentPrice =
    quote?.currentPrice !== undefined &&
    (status === "valid" || status === "stale") &&
    !isCrossCurrency
      ? toBoundedRendererNumber(parseExact(quote.currentPrice), {
          warnings,
          warningKeys,
          holdingId: holding.holdingId,
          field: "currentPrice",
        })
      : undefined;
  const costBasisExact = costBasisInPortfolioCurrency
    ? holdingCostBasisExact(holding)
    : null;
  const costBasis =
    costBasisExact === null
      ? undefined
      : toBoundedRendererNumber(costBasisExact, {
          warnings,
          warningKeys,
          holdingId: holding.holdingId,
          field: "costBasis",
        });
  const averagePrice =
    costBasisInPortfolioCurrency &&
    holding.costBasis?.mode === "average"
      ? toBoundedRendererNumber(
          parseExact(holding.costBasis.averageBuyPrice),
          {
            warnings,
            warningKeys,
            holdingId: holding.holdingId,
            field: "averagePrice",
          },
        )
      : undefined;
  const dailyReturnPct =
    (!isCrossCurrency || hasTrustedProviderValuation) &&
    quote !== undefined &&
    (status === "valid" || status === "stale") &&
    quote.dailyReturnPct !== undefined
      ? toBoundedRendererNumber(parseExact(quote.dailyReturnPct), {
          warnings,
          warningKeys,
          holdingId: holding.holdingId,
          field: "dailyReturnPct",
        })
      : null;
  const dailyChangeAmount =
    (!isCrossCurrency || hasTrustedProviderValuation) &&
    quote !== undefined &&
    (status === "valid" || status === "stale") &&
    quote.dailyChangeAmount !== undefined
      ? toBoundedRendererNumber(parseExact(quote.dailyChangeAmount), {
          warnings,
          warningKeys,
          holdingId: holding.holdingId,
          field: "dailyChangeAmount",
        })
      : undefined;
  const displayName = holdingName(holding);
  const quoteObservation = projectQuoteObservation(holding, quote);

  return {
    marketValueExact,
    snapshot: {
      instrumentId,
      ticker: holdingTicker(holding),
      name: displayName,
      displayName,
      marketValue,
      marketValueKnown: marketValueExact !== null,
      dailyReturnPct,
      quoteStatus: status,
      quantity,
      ...(averagePrice !== undefined ? { averagePrice } : {}),
      ...(currentPrice !== undefined ? { currentPrice } : {}),
      ...(costBasis !== undefined ? { costBasis } : {}),
      ...(dailyChangeAmount !== undefined ? { dailyChangeAmount } : {}),
      ...(quoteObservation !== undefined ? { quoteObservation } : {}),
    },
  };
}

function quoteMapForDocument(
  document: PortfolioDocumentV1,
  quoteSet: ManualQuoteSetV1 | undefined,
  warnings: PortfolioProjectionWarning[],
  warningKeys: Set<string>,
): ReadonlyMap<string, ManualQuoteV1> {
  if (!quoteSet) {
    return new Map();
  }

  if (
    quoteSet.portfolioId !== document.portfolioId ||
    quoteSet.currency !== document.currency
  ) {
    addWarning(warnings, warningKeys, {
      code: "quote_set_mismatch",
      field: "manualQuotes",
      message:
        "포트폴리오와 일치하지 않는 수동 시세 묶음을 안전하게 무시했습니다.",
    });
    return new Map();
  }

  const quotes = new Map<string, ManualQuoteV1>();
  for (const quote of quoteSet.quotes) {
    // Runtime validation rejects duplicates. Keeping the first value also
    // makes this boundary deterministic if a caller bypasses validation.
    if (!quotes.has(quote.holdingId)) {
      quotes.set(quote.holdingId, quote);
    }
  }
  return quotes;
}

function quoteSetMatchesDocument(
  document: PortfolioDocumentV1,
  quoteSet: ManualQuoteSetV1 | undefined,
): quoteSet is ManualQuoteSetV1 {
  return (
    quoteSet !== undefined &&
    quoteSet.portfolioId === document.portfolioId &&
    quoteSet.currency === document.currency
  );
}

/**
 * Projects a validated v1 source document into the finite-number renderer
 * model. Decimal multiplication and aggregation remain exact until this final
 * display boundary.
 */
export function projectPortfolioToRenderer(
  document: PortfolioDocumentV1,
  quoteSet?: ManualQuoteSetV1,
): PortfolioRendererProjection {
  const warnings: PortfolioProjectionWarning[] = [];
  const warningKeys = new Set<string>();
  const quotes = quoteMapForDocument(
    document,
    quoteSet,
    warnings,
    warningKeys,
  );
  const appearances: AppearanceOverrides = {};
  const projectedHoldings = document.holdings.map((holding) => {
    const projected = createHoldingSnapshot(
      holding,
      quotes.get(holding.holdingId),
      document.currency,
      warnings,
      warningKeys,
    );
    const instrumentId = projected.snapshot.instrumentId;
    const customization = normalizeAppearanceOverride({
      ...(holding.customization?.basePaletteId
        ? { paletteId: holding.customization.basePaletteId }
        : {}),
      ...(holding.customization?.baseHatId
        ? { hatId: holding.customization.baseHatId }
        : {}),
      ...(holding.customization?.paletteId
        ? { paletteId: holding.customization.paletteId }
        : {}),
      ...(holding.customization?.bodyColor
        ? { bodyColor: holding.customization.bodyColor }
        : {}),
      ...(holding.customization?.accentColor
        ? { accentColor: holding.customization.accentColor }
        : {}),
      ...(holding.customization?.patternId
        ? { patternId: holding.customization.patternId }
        : {}),
      ...(holding.customization?.hatId
        ? { hatId: holding.customization.hatId }
        : {}),
      ...(holding.customization?.nickname
        ? { nickname: holding.customization.nickname }
        : {}),
    });

    if (Object.keys(customization).length > 0) {
      appearances[instrumentId] = customization;
    }

    return projected;
  });

  const needsFarmUnitPersistence = document.farmUnit === undefined;
  const sourceFarmUnit = document.farmUnit
    ? parseExact(document.farmUnit)
    : deriveFarmUnitExact(
        projectedHoldings
          .map(({ marketValueExact }) => marketValueExact)
          .filter((value): value is InternalDecimal => value !== null),
      );
  const persistence = sourceFarmUnit
    ? toFarmUnitPersistenceValue(sourceFarmUnit)
    : { value: FALLBACK_FARM_UNIT, clamped: false };

  if (needsFarmUnitPersistence) {
    addWarning(warnings, warningKeys, {
      code: "farm_unit_derived",
      field: "farmUnit",
      message:
        "첫 평가액 분포로 성장 기준을 계산했습니다. 다음 저장 때 이 값을 포트폴리오에 고정해야 합니다.",
    });
  }

  if (persistence.clamped) {
    addWarning(warnings, warningKeys, {
      code: "farm_unit_persistence_clamped",
      field: "farmUnit",
      message:
        "계산된 성장 기준이 저장 범위를 넘어 포트폴리오 계약의 최대값으로 제한되었습니다.",
    });
  }

  const farmUnit = toBoundedRendererNumber(
    parseExact(persistence.value),
    {
      warnings,
      warningKeys,
      field: "farmUnit",
    },
  );
  const fallbackAsOf = document.updatedAt;
  const acceptedQuoteSet = quoteSetMatchesDocument(document, quoteSet)
    ? quoteSet
    : undefined;

  return {
    portfolio: {
      portfolioId: document.portfolioId,
      currency: document.currency,
      asOf: acceptedQuoteSet?.asOf ?? fallbackAsOf,
      marketDate: acceptedQuoteSet?.marketDate ?? fallbackAsOf.slice(0, 10),
      farmUnit: farmUnit > 0 ? farmUnit : 1,
      positions: projectedHoldings.map(({ snapshot }) => snapshot),
    },
    appearanceOverrides: appearances,
    warnings,
    needsFarmUnitPersistence,
    farmUnitPersistenceValue: persistence.value,
  };
}

export function projectLocalPortfolioRecordToRenderer(
  record: LocalPortfolioRecordV1,
): PortfolioRendererProjection {
  return projectPortfolioToRenderer(record.document, record.manualQuotes);
}

/** Compatibility name for callers that describe the operation as adaptation. */
export const adaptPortfolioToRenderer = projectPortfolioToRenderer;
